import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL + '/api';

// Create axios instances
const publicAxios = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

const protectedAxios = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Add request interceptor to update CSRF token
protectedAxios.interceptors.request.use(
    async config => {
        try {
            const csrfResponse = await fetch(`${API_URL}/login/`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (!csrfResponse.ok) {
                throw new Error('Failed to get CSRF token');
            }

            const csrfData = await csrfResponse.json();
            
            // Get the CSRF token from either the cookie or the response
            const csrfToken = csrfData.csrfToken;

            if (!csrfToken) {
                throw new Error('No CSRF token available');
            }
            localStorage.setItem('csrfToken', csrfToken);


            // Set the headers
            config.headers['X-CSRFToken'] = csrfToken;
            return config;
        } catch (error) {
            console.error('Request interceptor error:', error);
            return Promise.reject(error);
        }
    },
    error => Promise.reject(error)
);

const ProductAPI = {
    // Public routes - no auth needed
    getAllProducts: async () => {
        try {
            const response = await publicAxios.get('/products/');
            return response.data;
        } catch (error) {
            console.error('Get all products error:', error.response?.data || error.message);
            throw new Error('Failed to fetch products');
        }
    },

    getProduct: async (id) => {
        try {
            const response = await publicAxios.get(`/products/${id}/`);
            return response.data;
        } catch (error) {
            console.error('Get product error:', error.response?.data || error.message);
            throw new Error(`Failed to fetch product with id ${id}`);
        }
    },

    // Protected routes - require auth
    createProduct: async (productData) => {
        try {
            const formData = new FormData();
            for (const [key, value] of Object.entries(productData)) {
                if (key === 'photos' && Array.isArray(value)) {
                    value.forEach(photo => {
                        formData.append('images', photo);
                    });
                } else {
                    formData.append(key, value);
                }
            }
            
            const response = await protectedAxios.post('/products/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            
            return response.data;
        } catch (error) {
            console.error('Create product error:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            throw new Error(error.response?.data?.detail || 'Failed to create product');
        }
    },

    updateProduct: async (id, productData) => {
        try {
            const formData = new FormData();
            
            // Handle basic fields
            const basicFields = ['name', 'price', 'description', 'type'];
            basicFields.forEach(field => {
                if (productData[field] !== undefined) {
                    formData.append(field, productData[field]);
                }
            });

            // Handle new photos/images
            if (productData.photos) {
                const newPhotos = productData.photos.filter(photo => photo instanceof File);
                newPhotos.forEach(photo => {
                    formData.append('images', photo);
                });
            }

            const response = await protectedAxios.put(`/products/${id}/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            
            return response.data;
        } catch (error) {
            console.error('Update product error:', error.response?.data || error.message);
            throw new Error(`Failed to update product with id ${id}`);
        }
    },

    deleteProduct: async (id) => {
        try {
            await protectedAxios.delete(`/products/${id}/`);
            return true;
        } catch (error) {
            console.error('Delete product error:', error.response?.data || error.message);
            throw new Error(`Failed to delete product with id ${id}`);
        }
    },

    deleteProductImage: async (productId, filename) => {
        try {
            await protectedAxios.delete(`/products/${productId}/delete_image/?image_filename=${filename}`);
            return true;
        } catch (error) {
            console.error('Delete product image error:', error.response?.data || error.message);
            throw new Error(`Failed to delete image ${filename} from product ${productId}`);
        }
    }
};

const ShippingAPI = {
    getShippingRates: async (postalCode) => {
        try {
            const response = await publicAxios.get(`/shipping/rates/${postalCode}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
};

const PaymentAPI = {
    processPayment: async (paymentData) => {
        try {
            // Create a new axios instance just for this request
            const response = await axios.post(`${API_URL}/pay/`, paymentData, {
                headers: {
                    'Content-Type': 'application/json',
                },
                withCredentials: false  // Disable credentials
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }
};

const EmailAPI = {
    contactEmail: async (formData) => {
        try {
            // Create a new axios instance just for this request
            console.log(`${API_URL}/contact/`)
            const response = await axios.post(`${API_URL}/contact/`, {
                name: formData.name,
                email: formData.email,
                jewelryType: formData.jewelryType,
                metalPreference: formData.metalPreference,
                budget: formData.budget,
                timeframe: formData.timeframe,
                description: formData.description
            }, {
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: false  // Disable credentials
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }
};

const API = {
    ...ShippingAPI,
    ...ProductAPI,
    ...PaymentAPI,
    ...EmailAPI
};

export default API;